import { AxiosError } from "axios";
import client from "./client";

/**
 * Wallcraft production totals (TAS-410/411/412).
 *
 * Every endpoint here is tenant-gated server-side (`isProductionPayEnabled()`) and
 * 404s on every other tenant, so callers must only reach them under
 * `config.productionPay?.enabled === true`.
 *
 * The API answers business failures as a 400 carrying `errorMessage` (the daily-cap
 * rejection of Rule 4 is the common one), so each helper resolves to
 * `{ success, errorMessage }` rather than throwing — the console reverts the field
 * and toasts the message.
 */

export interface ProductionPayResult {
  success: boolean;
  errorMessage?: string;
  /** Entry upsert only: the accepted row's id (null after a clear). The console
   *  applies commits locally instead of reloading, so the id must ride back. */
  miscEmployeeTaskID?: number | null;
}

export interface UpsertProductionEntryBody {
  employeeID: number;
  payPeriodID: number;
  /** The punch's LOCAL day, `YYYY-MM-DD`. Must fall inside `payPeriodID`. */
  entryDate: string;
  projectID: number;
  accountCodeID: number;
  /** `null` clears the entry (deletes the MiscEmployeeTask row and its link). */
  amount: number | null;
}

export interface SetPoNumberBody {
  employeeID: number;
  payPeriodID: number;
  entryDate: string;
  projectID: number;
  /** Digits only, ≤ 10 chars — TimeEntryHourly.ServiceOrderText is varchar(10). `null` clears. */
  poNumber: string | null;
}

export interface SplitDaySegment {
  accountCodeID: number;
  hours: number;
}

export interface SplitDayBody {
  employeeID: number;
  payPeriodID: number;
  entryDate: string;
  projectID: number;
  /** Σ hours must equal the day's punched total for that job, to the minute. */
  segments: SplitDaySegment[];
}

export function getProductionPayErrorMessage(
  error: unknown,
  fallback = "Could not save — please try again."
): string {
  if (error && typeof error === "object" && "response" in error) {
    const ax = error as AxiosError<{ errorMessage?: string; message?: string }>;
    const msg = ax.response?.data?.errorMessage ?? ax.response?.data?.message;
    if (typeof msg === "string" && msg.length > 0) return msg;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/** Normalizes both wire shapes: a 200 with `{ success:false, errorMessage }` and a 4xx throw. */
function toResult(data: unknown, fallback: string): ProductionPayResult {
  const body = (data ?? {}) as {
    success?: boolean; errorMessage?: string; message?: string; miscEmployeeTaskID?: number | null;
  };
  if (body.success === false) {
    return { success: false, errorMessage: body.errorMessage ?? body.message ?? fallback };
  }
  return body.miscEmployeeTaskID !== undefined
    ? { success: true, miscEmployeeTaskID: body.miscEmployeeTaskID }
    : { success: true };
}

/** Upsert the `Total $` on one (employee · day · job · account code). `amount: null` clears it. */
export async function upsertProductionEntry(
  body: UpsertProductionEntryBody
): Promise<ProductionPayResult> {
  const fallback = "Could not save the total.";
  try {
    const response = await client.put("/admin/productionPay/entry", body);
    return toResult(response.data, fallback);
  } catch (error: unknown) {
    return { success: false, errorMessage: getProductionPayErrorMessage(error, fallback) };
  }
}

/** Stamp (or clear) the PO # on every punch of that local day on the job's PO account code. */
export async function setPoNumber(body: SetPoNumberBody): Promise<ProductionPayResult> {
  const fallback = "Could not save the PO #.";
  try {
    const response = await client.put("/admin/productionPay/poNumber", body);
    return toResult(response.data, fallback);
  } catch (error: unknown) {
    return { success: false, errorMessage: getProductionPayErrorMessage(error, fallback) };
  }
}

/** Re-code / split one (employee · local day · job) across account codes. */
export async function splitDay(body: SplitDayBody): Promise<ProductionPayResult> {
  const fallback = "Could not save the split.";
  try {
    const response = await client.post("/admin/productionPay/splitDay", body);
    return toResult(response.data, fallback);
  } catch (error: unknown) {
    return { success: false, errorMessage: getProductionPayErrorMessage(error, fallback) };
  }
}
